/* ============================================================
   CLX Animations — Claxton Bakery
   - Light fade for sections / collection cards that start below the fold
     (.clx-pre is added only by JS, so nothing is hidden without it)
   - Hooks into product variant change for price flash & image cross-fade
   - Theme editor: content is never hidden
   ============================================================ */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (REDUCED) return; // .clx-anim-ready never set in head — content stays visible

  var SUPPORTS_IO = 'IntersectionObserver' in window;
  var PRE_CLASS = 'clx-pre';

  // Sections that get a light fade when they start below the fold
  var SECTION_SELECTORS = [
    '.clx-fci',
    '.clx-fi',
    '.clx-vh',
    '.clx-ft',
    '.clx-prod',
    '.clx-col',
    '.clx-pf'
  ];

  // Collection cards fade individually (a grid can be many screens tall)
  var ITEM_SELECTORS = [
    '.clx-col .clx-col__card'
  ];

  var ALL_SELECTORS = SECTION_SELECTORS.concat(ITEM_SELECTORS).join(',');

  // Fade starts while the element is still ~15% of a screen below the fold,
  // so by the time it is visible the fade is nearly done
  var observer = SUPPORTS_IO ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.remove(PRE_CLASS);
        observer.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px 15% 0px', threshold: 0 }) : null;

  function observeSections(root) {
    if (!observer) return; // no IO: nothing is ever hidden

    Array.prototype.forEach.call((root || document).querySelectorAll(ALL_SELECTORS), function (el) {
      // Only content that starts below the fold is hidden — anything visible on load stays untouched
      if (el.getBoundingClientRect().top > window.innerHeight * 1.15) {
        el.classList.add(PRE_CLASS);
        observer.observe(el);
      }
    });
  }

  function revealAll(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('.' + PRE_CLASS), function (el) {
      el.classList.remove(PRE_CLASS);
      if (observer) observer.unobserve(el);
    });
  }

  /* --------------------------------------------------
     Product variant change: price flash + image fade
     -------------------------------------------------- */
  function hookProductPage() {
    document.addEventListener('change', function (e) {
      var sel = e.target;
      if (!sel || !sel.classList || !sel.classList.contains('clx-prod__variant-select')) return;

      var section = sel.closest('.clx-prod');
      if (!section) return;

      // Defer slightly so the section's own variant handler updates
      // the DOM first.
      setTimeout(function () {
        var price = section.querySelector('.clx-prod__price');
        if (price) {
          price.classList.remove('clx-anim-flash');
          // Force reflow so the animation can replay on subsequent changes
          // eslint-disable-next-line no-unused-expressions
          void price.offsetWidth;
          price.classList.add('clx-anim-flash');
        }
      }, 30);
    });

    // Cross-fade product image when src changes — wrap the section's
    // direct src assignment by intercepting via MutationObserver
    if (!('MutationObserver' in window)) return;

    Array.prototype.forEach.call(document.querySelectorAll('.clx-prod__img'), function (img) {
      var lastSrc = img.getAttribute('src');
      var mo = new MutationObserver(function () {
        var newSrc = img.getAttribute('src');
        if (newSrc && newSrc !== lastSrc) {
          lastSrc = newSrc;
          // Briefly fade out, then fade in once the new image loads
          img.classList.add('clx-anim-img-out');
          var revert = function () {
            img.classList.remove('clx-anim-img-out');
            img.removeEventListener('load', revert);
          };
          if (img.complete && img.naturalWidth > 0) {
            // Image loaded from cache instantly
            setTimeout(revert, 200);
          } else {
            img.addEventListener('load', revert);
            // Safety timeout
            setTimeout(revert, 1200);
          }
        }
      });
      mo.observe(img, { attributes: true, attributeFilter: ['src'] });
    });
  }

  /* --------------------------------------------------
     Init
     -------------------------------------------------- */
  function init() {
    // Theme editor: never hide anything
    if (!(window.Shopify && window.Shopify.designMode)) observeSections(document);
    hookProductPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Safety net: reveal everything when printing or when the page is restored from the back/forward cache
  window.addEventListener('pageshow', function (e) { if (e.persisted) revealAll(document); });
  window.addEventListener('beforeprint', function () { revealAll(document); });
})();
